import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { addDays, monthGrid, monthLabel, nextMondayDate, shiftMonth, thisWeekendDate, weekdayInitials, weekStartDay, type LocalDate } from "@/lib/dates";
import { useStored } from "@/lib/storage";
import { DEFAULTS, PREF } from "@/lib/prefs";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";

export function MonthGrid({
  value,
  today,
  onSelect,
  min,
}: {
  value: LocalDate | null;
  today: LocalDate;
  onSelect: (d: LocalDate) => void;
  min?: LocalDate;
}) {
  const [month, setMonth] = React.useState(() => `${(value ?? today).slice(0, 7)}-01`);
  // Subscribed so an open picker follows Settings → Week starts on.
  useStored(PREF.weekStart, DEFAULTS.weekStart);
  const weekStart = weekStartDay();
  const weeks = monthGrid(month, weekStart);
  return (
    <div className="w-[236px] select-none">
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-[13px] font-medium">{monthLabel(month)}</span>
        <div className="flex">
          <Button size="icon-sm" variant="quiet" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeft />
          </Button>
          <Button size="icon-sm" variant="quiet" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRight />
          </Button>
        </div>
      </div>
      <div role="grid" aria-label={monthLabel(month)} className="grid grid-cols-7 gap-y-0.5 text-center">
        {weekdayInitials(weekStart).map((d, i) => (
          <div key={i} className="pb-1 text-[11px] font-medium text-subtle" aria-hidden>
            {d}
          </div>
        ))}
        {weeks.flat().map((d) => {
          const inMonth = d.slice(0, 7) === month.slice(0, 7);
          const selected = d === value;
          const isToday = d === today;
          const disabled = !!min && d < min;
          return (
            <button
              key={d}
              type="button"
              role="gridcell"
              aria-selected={selected}
              aria-label={d}
              disabled={disabled}
              onClick={() => onSelect(d)}
              className={cn(
                "mx-auto inline-flex size-8 items-center justify-center rounded-md text-[13px] tabular transition-colors hover:bg-hover disabled:opacity-30",
                !inMonth && "text-subtle/70",
                isToday && !selected && "font-semibold text-foreground underline decoration-2 underline-offset-4",
                selected && "bg-primary font-medium text-primary-foreground hover:bg-primary",
              )}
            >
              {Number(d.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface DatePickerProps {
  value: LocalDate | null;
  today: LocalDate;
  onChange: (d: LocalDate | null) => void;
  children: React.ReactNode;
  /** Label for the clear action. */
  clearLabel?: string;
  quick?: ("today" | "tomorrow" | "weekend" | "nextWeek")[];
  align?: "start" | "center" | "end";
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}

export function DatePicker({
  value,
  today,
  onChange,
  children,
  clearLabel = "None",
  quick = ["today", "tomorrow", "weekend", "nextWeek"],
  align = "start",
  open,
  onOpenChange,
}: DatePickerProps) {
  const [innerOpen, setInnerOpen] = React.useState(false);
  const isOpen = open ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const pick = (d: LocalDate | null) => {
    onChange(d);
    setOpen(false);
  };
  const quickDates: Record<string, { label: string; date: LocalDate }> = {
    today: { label: "Today", date: today },
    tomorrow: { label: "Tomorrow", date: addDays(today, 1) },
    weekend: { label: "This weekend", date: thisWeekendDate(today) },
    nextWeek: { label: "Next week", date: nextMondayDate(today) },
  };
  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-2" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex flex-wrap gap-1">
          {quick.map((q) => (
            <Button
              key={q}
              size="xs"
              variant={value === quickDates[q]!.date ? "default" : "secondary"}
              onClick={() => pick(quickDates[q]!.date)}
            >
              {quickDates[q]!.label}
            </Button>
          ))}
          {value && (
            <Button size="xs" variant="quiet" onClick={() => pick(null)}>
              {clearLabel}
            </Button>
          )}
        </div>
        <MonthGrid value={value} today={today} onSelect={(d) => pick(d)} />
      </PopoverContent>
    </Popover>
  );
}
